import React from 'react';
import { Task, PleriTaskStatus } from '../../api/tasks';
import { useListConversations } from '../../api/conversations';
import { getNextScheduledTime } from './utils';
import DateWithTooltip from '../../components/common/DateWithTooltip';
import { ConversationStatusBadge } from '../../components/common/ConversationStatusBadge';
import { ClockIcon } from '@heroicons/react/24/outline';
import { LoadingState } from '../../components/common/LoadingState';
import { TaskStatusBadge } from '../../components/common/TaskStatusBadge';
import { Tooltip } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

interface TaskCardProps {
  task: Task;
  onClick: () => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, onClick }) => {
  const { t } = useTranslation('pleri/pages');
  // Get last execution info
  const { data: lastExecutionData, isLoading } = useListConversations({
    source: 'task',
    sourceId: task.id,
    limit: 1,
  });

  const lastExecution = lastExecutionData?.conversations?.[0];
  const nextScheduledTime = getNextScheduledTime(task.schedule);
  const isTaskDisabled = task.status !== PleriTaskStatus.Active;

  return (
    <div
      onClick={onClick}
      className="group min-h-[200px] cursor-pointer rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all duration-200 hover:border-orange-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800"
      data-testid="task-card"
    >
      {isLoading ? (
        <>
          <LoadingState text={t('common:loading')} />
        </>
      ) : (
        <div className="flex h-full flex-col">
          <div className="mb-4 flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 line-clamp-2 text-base font-semibold text-gray-900 transition-colors group-hover:text-orange-600 dark:text-gray-100 dark:group-hover:text-gray-50">
                {task.name}
              </h3>
              {task.description && (
                <p className="line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                  {task.description}
                </p>
              )}
            </div>
            <TaskStatusBadge status={task.status} />
          </div>

          {/* Stats */}
          <div className="mt-auto space-y-1 border-gray-100 dark:border-gray-800">
            {/* Last Execution */}
            {lastExecution ? (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{t('lastExecuted')}</span>
                <div className="flex items-center gap-2">
                  <DateWithTooltip date={lastExecution.createdAt} />
                  <ConversationStatusBadge status={lastExecution.status} />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  {t('lastExecuted')}
                </span>
                <span className="text-gray-400 dark:text-gray-500">
                  {t('never')}
                </span>
              </div>
            )}

            {/* Next Run */}
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <ClockIcon className="h-4 w-4" />
                {t('nextRun')}
              </span>
              {isTaskDisabled ? (
                <Tooltip label={t('taskIsPausedActivateTheTaskTo')}>
                  <span className="cursor-help text-gray-400 dark:text-gray-500">
                    N/A
                  </span>
                </Tooltip>
              ) : nextScheduledTime ? (
                <DateWithTooltip date={nextScheduledTime} />
              ) : (
                <span className="text-gray-400 dark:text-gray-500">
                  {t('notScheduled')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
